/** The fighter: the stick, the fire button, the Nova, and what happens between ships (docs/spec/02 §2; 03 §3). */

import { damageEnemy, shipXs } from './combat';
import { hitEmpress } from './empress';
import { clamp, cos, sin } from './math';
import { anyAloft, isDiving } from './roster';
import { FIELD, FIGHTER, NOVA } from './tuning';
import type { GameState, Input, Player } from './types';

export function newPlayer(): Player {
  return {
    x: FIELD.w / 2,
    status: 'arriving',
    timer: 0,
    dual: false,
    safe: 0,
    fireHeld: false,
    bombHeld: false,
    cooldown: 0,
    caughtX: 0,
    caughtY: 0,
    spin: 0,
  };
}

function volleysOut(s: GameState): number {
  const ids: number[] = [];
  for (const shot of s.shots) if (!ids.includes(shot.volley)) ids.push(shot.volley);
  return ids.length;
}

function fire(s: GameState): void {
  const p = s.player;
  const pw = s.powers;
  const rapid = pw.rapid > 0;
  if (volleysOut(s) >= (rapid ? NOVA.rapidVolleys : FIGHTER.maxVolleys)) return;
  const speed = FIGHTER.shotSpeed * (rapid ? NOVA.rapidShot : 1);
  const volley = s.nextVolley++;
  const pierce = pw.pierce > 0;
  const y = FIELD.fighterY - 10;
  const add = (x: number, angle: number): void => {
    s.shots.push({ x, y, vx: sin(angle) * speed, vy: -cos(angle) * speed, volley, pierce, struck: [], scored: false });
    s.stats.shots++;
  };
  for (const x of shipXs(s)) add(x, 0);
  if (pw.spread > 0) {
    add(p.x, -NOVA.spreadAngle);
    add(p.x, NOVA.spreadAngle);
  }
  s.events.push({ type: 'fire', x: p.x, y, dual: p.dual });
}

/** Spend a Nova: bombs vanish, everything in the air takes a hit, a moment's safety (03 §3). */
function nova(s: GameState): void {
  const p = s.player;
  s.powers.novas--;
  s.bombs = [];
  p.safe = Math.max(p.safe, NOVA.safeTicks);
  s.events.push({ type: 'nova', x: p.x, y: FIELD.fighterY });
  // Everything that is not sitting at home: on its way in, diving, or on its way out.
  for (const e of [...s.enemies]) if (!e.gone && isDiving(e)) damageEnemy(s, e, 1);
  hitEmpress(s, NOVA.novaEmpressDamage);
}

function fly(s: GameState, input: Input): void {
  const p = s.player;
  const edge = FIGHTER.margin + (p.dual ? FIGHTER.dualOffset : 0);
  const move = (input.right ? 1 : 0) - (input.left ? 1 : 0);
  p.x = clamp(p.x + move * FIGHTER.speed, edge, FIELD.w - edge);
  if (p.cooldown > 0) p.cooldown--;
  const rapid = s.powers.rapid > 0;
  // A press fires at once; under Rapid the button also fires for as long as it is down, and either
  // way no faster than Rapid's rate (tapping must not out-fire holding).
  const pressed = input.fire && !p.fireHeld && (!rapid || p.cooldown === 0);
  const held = input.fire && rapid && p.cooldown === 0;
  if (pressed || held) {
    fire(s);
    p.cooldown = NOVA.rapidEvery;
  }
  if (input.bomb && !p.bombHeld && s.mode === 'supernova' && s.powers.novas > 0) nova(s);
}

/** The next ship, or the end of the game. */
function nextShip(s: GameState): void {
  const p = s.player;
  if (s.lives <= 0) {
    s.phase = 'gameover';
    s.phaseT = 0;
    s.events.push({ type: 'gameOver' });
    return;
  }
  s.lives--;
  p.status = 'arriving';
  p.timer = 0;
  p.x = FIELD.w / 2;
  p.dual = false;
  // Nothing can touch it while READY is up: a straggler on its way out must not take the new ship.
  p.safe = FIGHTER.readyTicks;
  s.bombs = [];
  s.events.push({ type: 'ready' });
}

export function updatePlayer(s: GameState, input: Input): void {
  const p = s.player;
  p.timer++;
  if (p.safe > 0) p.safe--;
  switch (p.status) {
    case 'arriving':
      fly(s, input);
      if (p.status === 'arriving' && p.timer >= FIGHTER.readyTicks) {
        p.status = 'flying';
        p.timer = 0;
      }
      break;
    case 'flying':
      fly(s, input);
      break;
    case 'dead': {
      // Wait for the divers to go home, for a rescued ship to land and for FIGHTER CAPTURED to be
      // read, but not for ever.
      const quiet = !anyAloft(s) && !s.beam && !s.rescue && s.capturedMsg === 0;
      if (s.phase !== 'gameover' && ((p.timer >= FIGHTER.deathWait && quiet) || p.timer >= FIGHTER.deathWaitMax)) nextShip(s);
      break;
    }
    case 'caught':
      break;
  }
  p.fireHeld = input.fire;
  p.bombHeld = input.bomb;
}
